// src/utils/montages.ts — SCE Issues
// Qui monte chaque équipement (29/10, maquette
// https://claude.ai/artifact/PQFFkpfGUpLAJFos3qfLLd) : le montage Élec et le
// montage Méca, chacun « En interne » ou confié à une société de la liste
// (roles.societes, panneau d'administration). Vide = à attribuer.
//
// Tout coordinateur, suppléant ou administrateur règle les deux, quel que soit
// son métier (Guillaume, 29/10). La fiche équipement en montre un badge ; la
// donnée servira de filtre (statistiques, liste des problèmes) et à limiter la
// vue d'un externe aux équipements de sa société — lisibilité, pas sécurité.
//
// Un problème suit l'attribution ACTUELLE de son équipement : elle ne change
// quasiment jamais (Guillaume, 29/10), aucune colonne sur le problème.
//
// Rangement : bloc `montages` de la ligne `__global__` de SCE_UserPrefs, une
// entrée par N° de série (clé de rapprochement). Pur : ni React, ni réseau.

import { cleRapprochement } from '@/types/issue.types'

export const CLE_BLOC_MONTAGES = 'montages'
export const VERSION_MONTAGES = 1
export const EN_INTERNE = 'En interne'

export type MetierMontage = 'ELEC' | 'MECA'
export const METIERS_MONTAGE: readonly MetierMontage[] = ['ELEC', 'MECA']
export const LIBELLE_MONTAGE: Record<MetierMontage, string> = { ELEC: 'Élec', MECA: 'Méca' }

export interface Attribution {
  /** « En interne » ou le nom d'une société, tel que la liste l'écrit. */
  valeur: string
  par: string
  /** Date ISO. */
  le: string
}

export type MontagesEquipement = Partial<Record<MetierMontage, Attribution>>

export interface Montages {
  version: typeof VERSION_MONTAGES
  /** Par clé de rapprochement du N° de série. */
  equipements: Record<string, MontagesEquipement>
}

export const MONTAGES_VIDES: Montages = { version: VERSION_MONTAGES, equipements: {} }

const texte = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')
const estObjet = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

function lireAttribution(v: unknown): Attribution | null {
  if (!estObjet(v)) return null
  const valeur = texte(v.valeur)
  return valeur ? { valeur, par: texte(v.par), le: texte(v.le) } : null
}

/** Le bloc relu : entrée, montage ou valeur illisible écartés. Jamais d'exception. */
export function lireMontages(bloc: unknown): Montages {
  const brut = estObjet(bloc) && estObjet(bloc.equipements) ? bloc.equipements : {}
  const equipements: Record<string, MontagesEquipement> = {}
  for (const [ns, v] of Object.entries(brut)) {
    const cle = cleRapprochement(ns)
    if (!cle || !estObjet(v)) continue
    const lu: MontagesEquipement = {}
    for (const m of METIERS_MONTAGE) {
      const a = lireAttribution(v[m])
      if (a) lu[m] = a
    }
    if (Object.keys(lu).length) equipements[cle] = lu
  }
  return { version: VERSION_MONTAGES, equipements }
}

/** Le montage d'un équipement pour un métier, ou null (à attribuer). */
export function montageDe(montages: Montages, numeroSerie: string, metier: MetierMontage): Attribution | null {
  return montages.equipements[cleRapprochement(numeroSerie)]?.[metier] ?? null
}

/**
 * Le montage de ces équipements confié à `valeur` (« En interne » ou une
 * société) ; `null` le remet à attribuer. Les autres montages ne bougent pas.
 */
export function attribuer(
  montages: Montages,
  numerosSerie: readonly string[],
  metier: MetierMontage,
  valeur: string | null,
  par: string,
  le: string,
): Montages {
  const equipements = { ...montages.equipements }
  const nette = valeur?.trim() ?? ''
  for (const ns of numerosSerie) {
    const cle = cleRapprochement(ns)
    if (!cle) continue
    const ligne: MontagesEquipement = { ...equipements[cle] }
    if (nette) ligne[metier] = { valeur: nette, par, le }
    else delete ligne[metier]
    if (Object.keys(ligne).length) equipements[cle] = ligne
    else delete equipements[cle]
  }
  return { version: VERSION_MONTAGES, equipements }
}

/** Les choix d'un montage : « En interne », puis les sociétés qui le font. */
export function choixMontage(metier: MetierMontage, societes: readonly { nom: string; montages: readonly string[] }[]): string[] {
  return [EN_INTERNE, ...societes.filter((s) => s.montages.includes(metier)).map((s) => s.nom)]
}

/** Confié à une société (pas en interne, pas à attribuer). */
export function estSousTraite(a: Attribution | null | undefined): a is Attribution {
  return !!a && a.valeur !== EN_INTERNE
}

/** Un des deux montages reste à attribuer. */
export function resteAAttribuer(montages: Montages, numeroSerie: string): boolean {
  return METIERS_MONTAGE.some((m) => !montageDe(montages, numeroSerie, m))
}

/** Combien de ces équipements ont encore un montage à attribuer. */
export function nombreAAttribuer(montages: Montages, numerosSerie: readonly string[]): number {
  return numerosSerie.filter((ns) => resteAAttribuer(montages, ns)).length
}

/** Une des deux attributions est cette société. */
export function confieA(montages: Montages, numeroSerie: string, societe: string): boolean {
  return METIERS_MONTAGE.some((m) => montageDe(montages, numeroSerie, m)?.valeur === societe)
}

/** Filtre de la page : tous, à attribuer, ou une société. */
export type FiltreMontage = { genre: 'tous' } | { genre: 'attribuer' } | { genre: 'societe'; nom: string }

export function passeFiltre(montages: Montages, numeroSerie: string, filtre: FiltreMontage): boolean {
  if (filtre.genre === 'tous') return true
  if (filtre.genre === 'attribuer') return resteAAttribuer(montages, numeroSerie)
  return confieA(montages, numeroSerie, filtre.nom)
}

/** « Montage Élec de 3 équipements : Actemium » (message après l'écriture). */
export function messageAttribution(metier: MetierMontage, nombre: number, valeur: string | null): string {
  const quoi = nombre === 1 ? '1 équipement' : `${nombre} équipements`
  return `Montage ${LIBELLE_MONTAGE[metier]} de ${quoi} : ${valeur ?? 'à attribuer'}`
}
